import { Component, inject, input, model, OnInit, output, signal, WritableSignal } from '@angular/core';
import { FormGroup, ReactiveFormsModule } from '@angular/forms';
import { provideTranslocoScope } from '@jsverse/transloco';
import { sharedImports } from '../../../sharedImports';
import { MediumStore } from '@stores/medium.store';
import { FrameService, ManufacturerDTO, ManufacturerService, PaginatedFrameDTO, PaginatedManufacturerDTO } from '@api';
import { CustomsIdStore } from '@stores/customsId.store';
import { NumberErrorType } from '@compeople-shared/design-system';
import { FilterByPropertyPipe } from '@pipes/filter-by-property.pipe';
import { AutoErrorDirective } from '@directives/number-auto-error.directive';
import { hasCustomError, updateErrors } from '@utils/custom-error.utils';
import { lastValueFrom } from 'rxjs';
import { FrameOption } from '@utils/enums';

@Component({
  selector: 'art-artwork-meta-card',
  imports: [ReactiveFormsModule, sharedImports, FilterByPropertyPipe, AutoErrorDirective],
  templateUrl: './artwork-meta-card.component.html',
  styleUrls: ['./artwork-meta-card.component.scss'],
  providers: [provideTranslocoScope('artwork'), provideTranslocoScope('error')],
  standalone: true,
})
export class ArtworkMetaCardComponent implements OnInit {
  protected readonly FrameOption = FrameOption;
  protected readonly NumberErrorType = NumberErrorType;

  readonly manufacturerService = inject(ManufacturerService);
  readonly mediumStore = inject(MediumStore);
  readonly customsIdStore = inject(CustomsIdStore);
  readonly frameService = inject(FrameService);

  artworkMetaForm = input.required<FormGroup>();
  showCustomFrame = model(false);
  showCustomPassepartout = model(false);
  readonly = input(false);
  artist: string[] = [];
  medium: string[] = [];
  aidaPropertyOptions: { value: boolean; label: string }[] = [
    { value: true, label: 'yes' },
    { value: false, label: 'no' },
  ];

  autoErrors: Record<string, boolean> = {};
  customErrors = output<boolean>();

  paginatedFrame = signal<PaginatedFrameDTO | null>(null);
  paginatedManufacturer = signal<PaginatedManufacturerDTO | null>(null);

  async ngOnInit() {
    this.mediumStore.fetchMediums();
    await this.customsIdStore.fetchCustomsId();
    const [paginatedFrame, paginatedManufacturer] = await Promise.all([
      lastValueFrom(this.frameService.getFrames()),
      lastValueFrom(this.manufacturerService.getManufacturers()),
    ]);
    this.paginatedFrame.set(paginatedFrame);
    this.paginatedManufacturer.set(paginatedManufacturer);
  }

  readonly transformValue = (value: ManufacturerDTO) => ({
    label: value.firstName + ' ' + value.lastName,
  });

  searchManufacturers(event: CustomEvent) {
    const searchController = event.detail;

    const searchValue = searchController.value;

    const searchResults = this.filterSearchResults(searchValue);

    searchController.provideValues(this.buildSearchResults(searchResults));
  }

  filterSearchResults(searchValue: string) {
    const searchTerm = searchValue.trim();
    if (!searchTerm) {
      return this.paginatedManufacturer()?.elements;
    }

    return this.paginatedManufacturer()?.elements.filter((manufacturer: ManufacturerDTO) => {
      const name = manufacturer.firstName + ' ' + manufacturer.lastName;
      return name.toLowerCase().includes(searchTerm.toLowerCase());
    });
  }

  buildSearchResults(searchResults: ManufacturerDTO[] | undefined) {
    if (!searchResults || searchResults.length === 0) {
      return null;
    }
    return [{ label: null, options: searchResults }];
  }

  selectManufacturer(event: CustomEvent) {
    this.artworkMetaForm().patchValue({ artist: event.detail });
  }

  updateCustomField(
    event: CustomEvent,
    detailField: string,
    frameOption: FrameOption,
    showCustomInput: WritableSignal<boolean>,
  ) {
    if (event.detail === frameOption) {
      showCustomInput.set(true);
      this.artworkMetaForm().get(detailField)?.enable();
    } else {
      if (this.artworkMetaForm().value[detailField] != null) {
        this.artworkMetaForm().patchValue({ [detailField]: null });
      }
      showCustomInput.set(false);
      this.artworkMetaForm().get(detailField)?.disable();
    }
  }

  updatePassepartoutValues(event: CustomEvent) {
    this.updateCustomField(event, 'passepartoutDetails', FrameOption.CustomPassepartout, this.showCustomPassepartout);
  }

  updateFrameValues(event: CustomEvent) {
    this.updateCustomField(event, 'frameDetails', FrameOption.CustomFrame, this.showCustomFrame);
  }

  updateControlErrors(event: Record<string, boolean>) {
    this.autoErrors = updateErrors(this.autoErrors, event);
    this.customErrors.emit(hasCustomError(this.autoErrors));
  }
}
